import { useEffect } from 'react';
import type { ButtonProps, ButtonSize } from '@property.com.au/ui-kit';
import { Button, mediaHelper, MOBILE_MAX, paletteHelper, Text } from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import { WebLink } from '../../../../../app-kit/components/WebLink/WebLink';
import { WebLink as IWebLink } from '../../../../../app-kit/components/WebLink/types';
import { usePropertyPageModalContext } from '../../../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { trackModalImpression } from '../../../../../analytics/event-tracking/trackModalImpression';
import { useAccountQuery } from '../../../../../hooks/useAccountQuery';
import { isNotNullOrUndefinedValue } from '../../../../../helpers/isNotNullOrUndefinedValue';
import { replace } from '@rea-argonaut/link';
import { useUrlContext } from '../../../../../contexts/UrlContext/UrlContext';

const StyledButton = styled(Button)`
  width: 100%;
  ${mediaHelper('tabletMin')} {
    width: auto;
  }
  background-color: ${paletteHelper('backgroundPurpleStatic')};

  &:hover {
    background-color: ${paletteHelper('backgroundHoveredPurple')};
  }
  &:focus,
  &:active {
    background-color: ${paletteHelper('backgroundPressedPurple')};
  }
`;

const StyledWeblink = styled(WebLink)`
  ${mediaHelper('mobile')} {
    width: 100%;
  }
`;

export type GuestCta = {
  link: IWebLink;
};

export type MemberCta = {
  button: Omit<ButtonProps, 'sizeVariant' | 'iconPlacement'> & {
    text?: string;
    sizeVariant?: ButtonSize | null;
  };
  openModalHash: string;
  modal: {
    link: IWebLink;
    subtitle: string;
  };
};

type UpdatePropertiesDetailsProps = {
  updatePropertyDetails: {
    title: string;
    cta: GuestCta | MemberCta;
  };
};

export const isMemberCta = (cta: GuestCta | MemberCta): cta is MemberCta => 'button' in cta;

export const UpdatePropertyDetailsBrick: FunctionComponent<UpdatePropertiesDetailsProps> = ({
  updatePropertyDetails,
}) => {
  const { displayModal } = usePropertyPageModalContext();
  const { data } = useAccountQuery();
  const signedIn = isNotNullOrUndefinedValue(data?.account);
  const { cta } = updatePropertyDetails;
  const url = useUrlContext();

  useEffect(() => {
    if (!cta || !isMemberCta(cta)) return;
    if (signedIn && url.hash === cta.openModalHash) {
      trackModalImpression({
        user_action: 'opened',
        modal_name: 'update_property_details',
        screen_name: 'property_details',
        modal_is_fullscreen: window.innerWidth <= MOBILE_MAX + 1,
        in_page_source: 'property_page_property_features_brick',
      });
      displayModal({ name: 'updatePropertyDetailsModal' });
      replace(location.pathname + location.search);
    }
  }, [cta, signedIn, url.hash, displayModal]);
  return (
    <>
      <Text as="h3" variant="subtitle02">
        {updatePropertyDetails.title}
      </Text>
      {!signedIn && !isMemberCta(cta) && (
        <StyledWeblink
          {...cta.link}
          key={cta.link.key}
          clientSideRouting={false}
          rel="noopener nofollow"
        />
      )}
      {signedIn && isMemberCta(cta) && (
        <StyledButton
          {...cta.button}
          iconPlacement={undefined}
          sizeVariant={cta.button.sizeVariant ?? 'medium'}
          onClick={() => {
            trackModalImpression({
              user_action: 'opened',
              modal_name: 'update_property_details',
              screen_name: 'property_details',
              modal_is_fullscreen: window.innerWidth <= MOBILE_MAX + 1,
              in_page_source: 'property_page_property_features_brick',
            });
            displayModal({ name: 'updatePropertyDetailsModal' });
          }}
        >
          {cta.button.text}
        </StyledButton>
      )}
    </>
  );
};
